import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button, cn } from "@tellescope/ui";
import { Plus, Trash2 } from "lucide-react";
import type { VisibilityCondition } from "../../../shared/schema/visibility";
import { ConditionGroup } from "./ConditionGroup";
import { useConditionBuilder } from "./useConditionBuilder";

interface ConditionBuilderProps {
  /** The current condition value (controlled) */
  value?: VisibilityCondition;
  /** Called when the condition changes */
  onChange?: (condition: VisibilityCondition | undefined) => void;
  /** Additional class names */
  className?: string;
}

/**
 * A visual builder for creating visibility conditions.
 * Supports full AND/OR/NOT/$elemMatch recursive nesting with field conditions.
 */
export const ConditionBuilder = ({ value, onChange, className }: ConditionBuilderProps) => {
  const { t } = useTranslation();
  const builder = useConditionBuilder({
    initialCondition: value,
    onChange,
  });

  // Track the stringified value for deep comparison
  const valueStr = value !== undefined ? JSON.stringify(value) : undefined;
  const prevValueStr = useRef(valueStr);

  // Sync external value changes using deep comparison
  useEffect(() => {
    // Only sync if the external value actually changed (deep equality check)
    if (valueStr !== prevValueStr.current) {
      prevValueStr.current = valueStr;
      builder.setCondition(value);
    }
  }, [valueStr, value, builder.setCondition]);

  // Handle starting with an AND group
  const handleStartWithAnd = useCallback(() => {
    builder.initializeRoot("$and");
  }, [builder]);

  // Handle starting with an OR group
  const handleStartWithOr = useCallback(() => {
    builder.initializeRoot("$or");
  }, [builder]);

  // Handle starting with an elem match group
  const handleStartWithElemMatch = useCallback(() => {
    builder.initializeRootElemMatch("formResponse");
  }, [builder]);

  // Empty state - show buttons to start building
  if (builder.isEmpty) {
    return (
      <div className={cn("p-4 border border-dashed border-muted-foreground/30 rounded-md", className)}>
        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-3">
            {t("No visibility condition set. Block is always visible.")}
          </p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleStartWithAnd}>
              <Plus className="h-4 w-4 mr-1" />
              {t("Add AND condition")}
            </Button>
            <Button variant="outline" size="sm" onClick={handleStartWithOr}>
              <Plus className="h-4 w-4 mr-1" />
              {t("Add OR condition")}
            </Button>
            <Button variant="outline" size="sm" onClick={handleStartWithElemMatch}>
              <Plus className="h-4 w-4 mr-1" />
              {t("Add match item group")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render the condition tree
  return (
    <div className={cn("space-y-3", className)}>
      {/* Header with clear button */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{t("Visibility Condition")}</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground hover:text-destructive"
          onClick={builder.clear}
        >
          <Trash2 className="h-3 w-3 mr-1" />
          {t("Clear")}
        </Button>
      </div>

      {/* Condition tree */}
      {builder.condition && (
        <ConditionGroup
          condition={builder.condition}
          path={[]}
          onAddLeaf={builder.addLeafCondition}
          onAddGroup={builder.addLogicalGroup}
          onUpdateLeaf={builder.updateLeafCondition}
          onRemove={builder.removeCondition}
          onToggleOperator={builder.toggleLogicalOperator}
          onWrapInNot={builder.wrapInNot}
          onUnwrapNot={builder.unwrapNot}
          onAddElemMatchGroup={builder.addElemMatchGroup}
          onUpdateElemMatchSource={builder.updateElemMatchSource}
          onAddElemMatchCondition={builder.addElemMatchCondition}
          onUpdateElemMatchCondition={builder.updateElemMatchCondition}
          onRemoveElemMatchCondition={builder.removeElemMatchCondition}
          depth={0}
        />
      )}
    </div>
  );
};
